import {
	Analysis,
	determineExpirationDate,
	InvoiceCsvSkipEmptyLines,
	normalizeInvoiceHeader,
	RawInvoice,
	TimePeriod,
} from '@marmon/shared/types/analysis';
import { coerceCurrency, Currency } from '@marmon/shared/types/common';
import { User } from '@marmon/shared/types/user';
import { DocumentReference, Timestamp } from 'firebase/firestore';
import { parse, ParseResult } from 'papaparse';

/**
 * The expiration date of an analysis should be 180 days AFTER it was initially created.
 */
const AnalysisExpiresAtDuration = 180;

/**
 * Determines when a newly created analysis will expire: 180 calendar days
 * after today, at the end of that local day.
 *
 * @returns timestamp of when a newly created analysis will expire
 */
export function getAnalysisExpiration(now: Date = new Date()) {
	return Timestamp.fromDate(
		determineExpirationDate(now, AnalysisExpiresAtDuration),
	);
}

export function createAnalysis(
	payload: Pick<Analysis, 'name' | 'timePeriods' | 'costType'> & {
		currency?: Currency;
	},
	options: {
		expiresAt?: Timestamp;
		domain: string;
		createdBy: DocumentReference<User>;
		segments: string[];
	},
): Partial<Analysis> {
	const { name, costType } = payload;
	const { domain, createdBy, segments } = options;
	const expiresAt = options.expiresAt ?? getAnalysisExpiration();
	const currency = coerceCurrency(payload.currency);

	return {
		name,
		timePeriods: applyCurrencyToTimePeriods(payload.timePeriods, currency),
		domain,
		purgedAt: null,
		processedAt: null,
		erroredAt: null,
		shared: [createdBy],
		transactionCount: 0,
		costType,
		segments,
		expiresAt,
		currency,
	};
}

/**
 * Writes the analysis currency onto both time periods without changing amounts.
 */
export function applyCurrencyToTimePeriods(
	timePeriods: [TimePeriod, TimePeriod],
	currency: Currency,
): [TimePeriod, TimePeriod] {
	return timePeriods.map((period) => ({
		...period,
		currency,
	})) as [TimePeriod, TimePeriod];
}

export const parseFile = (source: File): Promise<ParseResult<RawInvoice>> =>
	new Promise((resolve, reject) => {
		parse<RawInvoice>(source, {
			header: true,
			skipEmptyLines: InvoiceCsvSkipEmptyLines,
			transformHeader: normalizeInvoiceHeader,
			complete: (res) => {
				resolve(res);
			},
			error: (error) => {
				reject(error);
			},
		});
	});

/**
 * Builds a download filename for the original analysis CSV.
 */
export function analysisCsvFileName(name: string) {
	const safe = name
		.trim()
		.replace(/[<>:"/\\|?*]+/g, '-')
		.slice(0, 80);
	return `${safe || 'analysis'}.csv`;
}

/**
 * Yields until the browser has painted the current change-detection cycle.
 * Call after flipping a loader on, and before CPU-bound work such as CSV parse.
 */
export function yieldForPaint(): Promise<void> {
	return new Promise((resolve) => {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => resolve());
		});
	});
}
